import React, { useState, useEffect, useCallback } from 'react'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import Avatar from '@mui/material/Avatar'
import Button from '@mui/material/Button'
import CircularProgress from '@mui/material/CircularProgress'
import Chip from '@mui/material/Chip'
import Paper from '@mui/material/Paper'
import { Link } from '@inertiajs/react'

interface AgentRef {
  id: number
  username: string
  slug: string
}

interface SessionEntry {
  entry_sequence: number
  model: string | null
  cwd: string | null
  tools_count: number
  permission_mode: string | null
}

interface ActivityEvent {
  id: number
  event_type: string
  agent: AgentRef
  trackable_type: string
  trackable_id: number
  metadata: Record<string, any>
  session_entry?: SessionEntry
  created_at: string
}

interface TaskActivityTimelineProps {
  boardSlug: string
  taskId: number
}

const AGENT_COLORS = [
  '#1976d2', '#388e3c', '#d32f2f', '#7b1fa2',
  '#f57c00', '#0097a7', '#5d4037', '#455a64'
]

function agentColor(agentId: number): string {
  return AGENT_COLORS[agentId % AGENT_COLORS.length]
}

function timeAgo(dateStr: string): string {
  const date = new Date(dateStr)
  const now = new Date()
  const seconds = Math.floor((now.getTime() - date.getTime()) / 1000)

  if (seconds < 60) return 'just now'
  const minutes = Math.floor(seconds / 60)
  if (minutes < 60) return `${minutes}m ago`
  const hours = Math.floor(minutes / 60)
  if (hours < 24) return `${hours}h ago`
  const days = Math.floor(hours / 24)
  if (days < 30) return `${days}d ago`
  return date.toLocaleDateString()
}

function formatDateTime(dateStr: string): string {
  const date = new Date(dateStr)
  return date.toLocaleString(undefined, {
    month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit'
  })
}

interface EventDescription {
  text: string
  link?: { href: string; label: string }
}

function describeEvent(event: ActivityEvent, boardSlug: string, taskId: number): EventDescription {
  const m = event.metadata

  switch (event.event_type) {
    case 'task_created':
      return { text: 'created this task' }

    case 'task_status_changed':
      return { text: `changed status from ${m.old_status} to ${m.new_status}` }

    case 'task_assigned':
      return { text: 'assigned this task' }

    case 'task_reviewer_changed':
      return { text: 'changed the reviewer' }

    case 'task_plan_updated':
      return { text: 'updated the plan' }

    case 'task_results_updated':
      return { text: 'updated the results' }

    case 'agent_notes_updated':
      return { text: 'updated agent notes' }

    case 'clarification_created':
      return {
        text: 'created a clarification',
        link: { href: `/tasks/boards/${boardSlug}/tasks/${m.task_id || taskId}/clarifications`, label: 'View' }
      }

    case 'clarification_resolved':
      return {
        text: 'resolved a clarification',
        link: { href: `/tasks/boards/${boardSlug}/tasks/${m.task_id || taskId}/clarifications`, label: 'View' }
      }

    case 'review_item_created':
      return {
        text: 'created a review item',
        link: { href: `/tasks/boards/${boardSlug}/tasks/${m.task_id || taskId}/review`, label: 'View' }
      }

    case 'review_item_resolved':
      return {
        text: 'resolved a review item',
        link: { href: `/tasks/boards/${boardSlug}/tasks/${m.task_id || taskId}/review`, label: 'View' }
      }

    case 'attachment_uploaded':
      return { text: `uploaded attachment "${m.filename || 'file'}"` }

    case 'sub_task_created':
      return {
        text: `created sub-task "${m.child_summary || ''}"`,
        link: m.child_task_id ? { href: `/tasks/boards/${boardSlug}/tasks/${m.child_task_id}`, label: 'View' } : undefined
      }

    case 'sub_task_completed':
      return {
        text: `sub-task "${m.child_summary || ''}" completed`,
        link: m.child_task_id ? { href: `/tasks/boards/${boardSlug}/tasks/${m.child_task_id}`, label: 'View' } : undefined
      }

    case 'session_started': {
      const entryParam = event.session_entry != null ? `?entry=${event.session_entry.entry_sequence}` : ''
      return {
        text: `started a session`,
        link: { href: `/monitoring/session_logs/${event.trackable_id}${entryParam}`, label: 'View session' }
      }
    }

    case 'session_completed':
      return {
        text: `completed a session (${m.status || 'done'})`,
        link: { href: `/monitoring/session_logs/${event.trackable_id}`, label: 'View session' }
      }

    case 'session_reopened': {
      const reopenEntryParam = event.session_entry != null ? `?entry=${event.session_entry.entry_sequence}` : ''
      return {
        text: 'resumed a session',
        link: { href: `/monitoring/session_logs/${event.trackable_id}${reopenEntryParam}`, label: 'View session' }
      }
    }

    default:
      return { text: event.event_type.replace(/_/g, ' ') }
  }
}

function StatusChip({ status }: { status: string }) {
  const colorMap: Record<string, string> = {
    pending: '#6B7280',
    planning: '#F59E0B',
    planned: '#D97706',
    in_progress: '#3B82F6',
    in_review: '#8B5CF6',
    completed: '#10B981',
    accepted: '#059669',
    rejected: '#EF4444',
  }
  return (
    <Chip
      label={status.replace(/_/g, ' ')}
      size="small"
      sx={{
        bgcolor: colorMap[status] || '#6B7280',
        color: 'white',
        height: 20,
        fontSize: '0.7rem',
        '& .MuiChip-label': { px: '6px', lineHeight: 1 }
      }}
    />
  )
}

export function TaskActivityTimeline({ boardSlug, taskId }: TaskActivityTimelineProps) {
  const [activities, setActivities] = useState<ActivityEvent[]>([])
  const [loading, setLoading] = useState(true)
  const [loadingMore, setLoadingMore] = useState(false)
  const [hasMore, setHasMore] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const fetchActivities = useCallback(async (beforeId?: number) => {
    try {
      const isLoadMore = !!beforeId
      if (isLoadMore) setLoadingMore(true)
      else setLoading(true)

      const params = new URLSearchParams({ limit: '50' })
      if (beforeId) params.set('before', String(beforeId))

      const resp = await fetch(
        `/api/v1/tasks/boards/${boardSlug}/tasks/${taskId}/activities?${params}`,
        {
          headers: { 'Accept': 'application/json' },
          credentials: 'same-origin',
        }
      )

      if (!resp.ok) throw new Error(`Failed to load activities: ${resp.status}`)

      const data = await resp.json()
      if (isLoadMore) {
        setActivities(prev => [...prev, ...data.activities])
      } else {
        setActivities(data.activities)
      }
      setHasMore(data.has_more)
      setError(null)
    } catch (err: any) {
      setError(err.message || 'Failed to load activities')
    } finally {
      setLoading(false)
      setLoadingMore(false)
    }
  }, [boardSlug, taskId])

  useEffect(() => {
    fetchActivities()
  }, [fetchActivities])

  const handleLoadMore = () => {
    if (activities.length > 0) {
      fetchActivities(activities[activities.length - 1].id)
    }
  }

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress size={28} />
      </Box>
    )
  }

  if (error) {
    return (
      <Box sx={{ py: 2 }}>
        <Typography color="error" variant="body2">{error}</Typography>
        <Button size="small" onClick={() => fetchActivities()} sx={{ mt: 1 }}>Retry</Button>
      </Box>
    )
  }

  if (activities.length === 0) {
    return (
      <Box sx={{ py: 3, textAlign: 'center' }}>
        <Typography variant="body2" color="text.secondary">No activity recorded yet.</Typography>
      </Box>
    )
  }

  return (
    <Box sx={{ py: 1 }}>
      {activities.map((event, index) => {
        const desc = describeEvent(event, boardSlug, taskId)
        const isLast = index === activities.length - 1

        return (
          <Box key={event.id} sx={{ display: 'flex', gap: 1.5, position: 'relative' }}>
            {/* Timeline line */}
            {!isLast && (
              <Box sx={{
                position: 'absolute',
                left: 15,
                top: 32,
                bottom: -8,
                width: 2,
                bgcolor: 'divider',
              }} />
            )}

            {/* Avatar */}
            <Avatar
              sx={{
                width: 30,
                height: 30,
                fontSize: '0.75rem',
                bgcolor: agentColor(event.agent.id),
                flexShrink: 0,
                mt: '2px',
              }}
            >
              {event.agent.username.charAt(0).toUpperCase()}
            </Avatar>

            {/* Content */}
            <Box sx={{ flex: 1, minWidth: 0, pb: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5, flexWrap: 'wrap' }}>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {event.agent.username}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {desc.text}
                </Typography>
                {desc.link && (
                  <Link href={desc.link.href} style={{ textDecoration: 'none' }}>
                    <Typography variant="body2" color="primary" sx={{ cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}>
                      {desc.link.label}
                    </Typography>
                  </Link>
                )}
              </Box>

              {/* Status transition chips */}
              {event.event_type === 'task_status_changed' && event.metadata.old_status && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                  <StatusChip status={event.metadata.old_status} />
                  <Typography variant="body2" color="text.secondary">{'\u2192'}</Typography>
                  <StatusChip status={event.metadata.new_status} />
                </Box>
              )}

              {/* Session entry details */}
              {event.session_entry && (
                <Paper
                  elevation={0}
                  sx={{
                    mt: 0.75,
                    px: 1.5,
                    py: 1,
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 1,
                    bgcolor: 'grey.50',
                  }}
                >
                  <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
                    {event.session_entry.model && (
                      <Box>
                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>Model</Typography>
                        <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>{event.session_entry.model}</Typography>
                      </Box>
                    )}
                    {event.session_entry.cwd && (
                      <Box sx={{ minWidth: 0 }}>
                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>Working Dir</Typography>
                        <Typography variant="body2" fontFamily="monospace" sx={{ fontSize: '0.75rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {event.session_entry.cwd}
                        </Typography>
                      </Box>
                    )}
                    {event.session_entry.tools_count > 0 && (
                      <Box>
                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>Tools</Typography>
                        <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>{event.session_entry.tools_count}</Typography>
                      </Box>
                    )}
                    {event.session_entry.permission_mode && (
                      <Box>
                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>Permissions</Typography>
                        <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>{event.session_entry.permission_mode}</Typography>
                      </Box>
                    )}
                  </Box>
                </Paper>
              )}

              {/* Timestamp */}
              <Typography variant="caption" color="text.secondary" title={formatDateTime(event.created_at)}>
                {timeAgo(event.created_at)}
              </Typography>
            </Box>
          </Box>
        )
      })}

      {hasMore && (
        <Box sx={{ textAlign: 'center', pt: 1 }}>
          <Button
            size="small"
            onClick={handleLoadMore}
            disabled={loadingMore}
            startIcon={loadingMore ? <CircularProgress size={14} /> : undefined}
          >
            {loadingMore ? 'Loading...' : 'Load more'}
          </Button>
        </Box>
      )}
    </Box>
  )
}
